/**
 * Meta Pixel (Facebook) — SEPARATE marketing analytics integration.
 *
 * This module is completely independent from the internal LupusLogix
 * conversion analytics (`src/lib/conversionTracker.ts`) and from the
 * /admin/metrics funnel. Nothing here writes to the database, and nothing in
 * the internal funnel reads from here.
 *
 * Privacy rules enforced by design:
 *  - The pixel is loaded ONLY on public acquisition/marketing routes. It is
 *    never loaded on authenticated wellness-data screens.
 *  - No parameters are ever sent with any event: no health/wellness data, no
 *    emails, names, user ids, plan details, notes, or page titles.
 *  - Advanced Matching and automatic form-field collection are NOT enabled.
 *  - Every call is wrapped so a blocked/failed Meta script can never affect
 *    application behavior.
 */

import { logger } from "@/lib/logger";

export const META_PIXEL_ID = "1301239415224870";

const SCRIPT_ID = "meta-pixel-script";
const PIXEL_SRC = "https://connect.facebook.net/en_US/fbevents.js";

type Fbq = ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean; version?: string; callMethod?: unknown; push?: unknown };
type PixelWindow = Window & { fbq?: Fbq; _fbq?: Fbq; __lupusMetaPixelInit?: boolean };

/** Public routes where the pixel may operate (acquisition experience only). */
const PUBLIC_PIXEL_PATHS = new Set<string>([
  "/",
  "/marketing",
  "/pricing",
  "/auth",
  "/trial-success",
  "/faq",
  "/help",
  "/credits",
  "/terms",
  "/privacy",
  "/medical-disclaimer",
  "/ai-wellness-disclaimer",
  "/data-breach-policy",
]);

/** Normalizes a location pathname; advertising params never reach this. */
export const normalizePixelPath = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

export const isPublicPixelPath = (pathname: string): boolean =>
  PUBLIC_PIXEL_PATHS.has(normalizePixelPath(pathname));

const getFbq = (): Fbq | null => {
  if (typeof window === "undefined") return null;
  const fbq = (window as PixelWindow).fbq;
  return typeof fbq === "function" ? fbq : null;
};

/**
 * Injects and initializes the pixel. Idempotent: safe across React re-renders,
 * route changes, and repeated calls — `fbq('init')` runs at most once.
 */
export const ensureMetaPixel = (): boolean => {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  const w = window as PixelWindow;
  if (w.__lupusMetaPixelInit) return true;

  try {
    if (!w.fbq) {
      const n = function (this: unknown, ...args: unknown[]) {
        const self = n as Fbq;
        if (self.callMethod) {
          (self.callMethod as (...a: unknown[]) => void).apply(self, args);
        } else {
          self.queue?.push(args);
        }
      } as Fbq;
      n.queue = [];
      n.loaded = true;
      n.version = "2.0";
      n.push = n;
      w.fbq = n;
      if (!w._fbq) w._fbq = n;
    }

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = PIXEL_SRC;
      document.head.appendChild(script);
    }

    // No Advanced Matching payload, and automatic config stays at Meta's
    // default without any form-field collection wired by us.
    w.fbq?.("init", META_PIXEL_ID);
    w.__lupusMetaPixelInit = true;
    return true;
  } catch {
    return false;
  }
};

/** Standard events used by this integration — no parameters, ever. */
type MetaStandardEvent = "PageView" | "ViewContent" | "StartTrial";

const track = (event: MetaStandardEvent): void => {
  try {
    if (!ensureMetaPixel()) return;
    getFbq()?.("track", event);
  } catch {
    /* Meta failures must never surface in the app */
  }
};

/** Verbose, non-PII diagnostics for the checkout conversion path. */
const pixelLog = (...args: unknown[]): void => {
  try {
    logger.info("[MetaPixel]", ...args);
  } catch {
    /* ignore */
  }
};

export const trackMetaPageView = (): void => track("PageView");
export const trackMetaViewContent = (): void => track("ViewContent");

// Keep direct conversion beacons alive across the immediate dashboard
// navigation. A module-level set prevents the Image objects from being
// garbage-collected before the browser starts their requests.
const activeConversionBeacons = new Set<HTMLImageElement>();

/**
 * Sends StartTrial through Meta's documented image-pixel transport. The
 * regular fbq transport can lose this conversion when the checkout return
 * redirects immediately after the library reports itself ready. This request
 * carries only the pixel id, standard event name, neutral confirmation URL,
 * and timestamp — no user, subscription, or wellness values.
 */
const sendMetaStartTrialBeacon = (): boolean => {
  if (typeof window === "undefined" || typeof Image === "undefined") return false;

  try {
    const beacon = new Image(1, 1);
    const release = () => activeConversionBeacons.delete(beacon);
    beacon.onload = release;
    beacon.onerror = release;
    activeConversionBeacons.add(beacon);

    const params = new URLSearchParams({
      id: META_PIXEL_ID,
      ev: "StartTrial",
      noscript: "1",
      dl: `${window.location.origin}/trial-success`,
      ts: Date.now().toString(),
    });
    beacon.src = `https://www.facebook.com/tr?${params.toString()}`;
    return true;
  } catch {
    return false;
  }
};

/**
 * Resolves once `fbevents.js` has actually loaded (queued calls are flushed by
 * the real library at that point). Resolves `false` on timeout — e.g. the
 * script is blocked by an ad blocker or the network is slow.
 */
const waitForPixelReady = (timeoutMs = 3000): Promise<boolean> =>
  new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve(false);
      return;
    }
    const isReady = () => !!(getFbq() as Fbq | null)?.callMethod;
    if (isReady()) {
      resolve(true);
      return;
    }
    const started = Date.now();
    const timer = window.setInterval(() => {
      if (isReady()) {
        window.clearInterval(timer);
        resolve(true);
      } else if (Date.now() - started >= timeoutMs) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, 100);
  });

const START_TRIAL_KEY = "lupuslogix_meta_start_trial_v2";
const START_TRIAL_PENDING_KEY = "lupuslogix_meta_start_trial_pending_v1";
const MAX_TRIAL_FINGERPRINTS = 20;

/** Small non-reversible digest so no raw identifier is ever persisted. */
const digest = (value: string): string => {
  let h = 5381;
  for (let i = 0; i < value.length; i += 1) {
    h = ((h << 5) + h + value.charCodeAt(i)) | 0;
  }
  return `t${(h >>> 0).toString(36)}`;
};

const readFingerprints = (): string[] => {
  try {
    const raw = localStorage.getItem(START_TRIAL_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
};

const rememberFingerprint = (key: string): void => {
  try {
    const existing = readFingerprints();
    if (existing.includes(key)) return;
    localStorage.setItem(
      START_TRIAL_KEY,
      JSON.stringify([...existing, key].slice(-MAX_TRIAL_FINGERPRINTS)),
    );
  } catch {
    /* storage unavailable — session guard still prevents duplicates */
  }
};

// Guards against a duplicate fire within the same page load even if storage
// is unavailable (private mode, quota errors).
const sessionFired = new Set<string>();

/**
 * Fires `StartTrial` exactly once per confirmed trial.
 *
 * `trialFingerprint` is an internal, non-identifying value (digested before
 * storage) used only for local de-duplication — it is NEVER sent to Meta, and
 * `StartTrial` carries no parameters. Because de-dup is per trial rather than
 * per browser, a legitimate future trial (another account, or a new trial
 * later) still converts on the same device.
 */
export const trackMetaStartTrialOnce = async (
  trialFingerprint: string,
): Promise<boolean> => {
  const key = digest(trialFingerprint || "unknown");
  pixelLog("StartTrial requested", { fingerprintKey: key });
  if (sessionFired.has(key)) {
    pixelLog("StartTrial skipped: already fired in this page session", { fingerprintKey: key });
    return false;
  }
  const stored = readFingerprints();
  if (stored.includes(key)) {
    pixelLog("StartTrial skipped: fingerprint already recorded in localStorage", {
      fingerprintKey: key,
      storedKeys: stored,
    });
    return false;
  }
  sessionFired.add(key);

  if (!ensureMetaPixel()) {
    pixelLog("StartTrial aborted: pixel could not initialize");
    sessionFired.delete(key);
    return false;
  }

  // Wait for the real library so the event is genuinely delivered instead of
  // sitting in the stub queue when the user navigates on.
  const ready = await waitForPixelReady();
  pixelLog("fbevents.js ready?", ready);
  if (!ready) {
    // Blocked/slow: remember it and retry on the next public page view.
    try {
      localStorage.setItem(START_TRIAL_PENDING_KEY, key);
    } catch {
      /* ignore */
    }
    pixelLog("StartTrial deferred: library not ready, queued as pending");
    sessionFired.delete(key);
    return false;
  }

  // Primary transport: the real Meta library (matches how PageView is sent).
  let sent = false;
  try {
    const fbq = getFbq();
    pixelLog("calling fbq('track','StartTrial')", { hasFbq: !!fbq });
    fbq?.("track", "StartTrial");
    sent = !!fbq;
    pixelLog("fbq('track','StartTrial') returned without error");
  } catch (err) {
    pixelLog("fbq track threw", err);
  }

  // Belt-and-braces fallback: documented image transport, in case the redirect
  // interrupts the library's own XHR/beacon.
  const beacon = sendMetaStartTrialBeacon();
  pixelLog("image beacon dispatched?", beacon);

  if (!sent && !beacon) {
    try {
      localStorage.setItem(START_TRIAL_PENDING_KEY, key);
    } catch {
      /* ignore */
    }
    sessionFired.delete(key);
    return false;
  }

  rememberFingerprint(key);
  try {
    localStorage.removeItem(START_TRIAL_PENDING_KEY);
  } catch {
    /* ignore */
  }
  pixelLog("StartTrial delivered and fingerprint recorded", { fingerprintKey: key });
  return true;
};

/**
 * Retries a `StartTrial` that could not be delivered earlier (pixel script not
 * loaded yet, or the user navigated away immediately). Safe to call on every
 * public page view: it is a no-op unless a pending trial is recorded, and the
 * fingerprint guard keeps it to one event per trial.
 */
export const flushPendingMetaStartTrial = (): void => {
  let key: string | null = null;
  try {
    key = localStorage.getItem(START_TRIAL_PENDING_KEY);
  } catch {
    return;
  }
  if (!key) return;
  if (readFingerprints().includes(key) || sessionFired.has(key)) {
    try {
      localStorage.removeItem(START_TRIAL_PENDING_KEY);
    } catch {
      /* ignore */
    }
    return;
  }
  if (!ensureMetaPixel()) return;
  void waitForPixelReady().then((ready) => {
    if (!ready) return;
    try {
      getFbq()?.("track", "StartTrial");
    } catch {
      /* ignore */
    }
    if (!sendMetaStartTrialBeacon()) return;
    pixelLog("pending StartTrial flushed", { fingerprintKey: key });
    sessionFired.add(key as string);
    rememberFingerprint(key as string);
    try {
      localStorage.removeItem(START_TRIAL_PENDING_KEY);
    } catch {
      /* ignore */
    }
  });
};

/**
 * Debug helper (console only): clears the LOCAL de-dup fingerprints so a repeat
 * real checkout on the same account/device can be observed end to end. It does
 * not send any event by itself — the next genuine trial return does that.
 */
export const resetMetaStartTrialDedup = (): void => {
  sessionFired.clear();
  try {
    localStorage.removeItem(START_TRIAL_KEY);
    localStorage.removeItem(START_TRIAL_PENDING_KEY);
  } catch {
    /* ignore */
  }
  pixelLog("local StartTrial de-dup cleared");
};

if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).__lupusResetMetaStartTrialDedup =
    resetMetaStartTrialDedup;
}
